import React, { useState, useEffect } from 'react';
import { TargetSelect } from './TargetSelect';

const MicrosegmentationScanner = () => {
  const [target, setTarget] = useState('');
  const [scanning, setScanning] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('form'); // 'form', 'history', 'results'
  const [scans, setScans] = useState([]);
  const [selectedScan, setSelectedScan] = useState(null);

  useEffect(() => {
    fetchScans();
  }, []);

  const fetchScans = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/scans/microsegmentation', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setScans(data);
      }
    } catch (err) {
      console.error('Error fetching AI scans:', err);
    }
  };

  const handleScan = async () => {
    if (!target) {
      setError('Por favor ingrese un dominio o IP');
      return;
    }

    setScanning(true);
    setError(null);
    setResults(null);

    try {
      const token = localStorage.getItem('token');
      
      const payload = {
        name: `Microsegmentación Scan - ${target}`,
        host: target,
        port_range: "1-1000",
        target_type: "web",
        description: "Microsegmentación de Red Assessment"
      };
      
      const response = await fetch('/api/scans/microsegmentation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error ejecutando escaneo');
      }

      const data = await response.json();
      setResults(data.results);
      setViewMode('results');
      fetchScans(); // Refresh history
    } catch (err) {
      setError(err.message);
    } finally {
      setScanning(false);
    }
  };

  const viewScanDetails = (scan) => {
    setSelectedScan(scan);
    setResults(scan.results);
    setViewMode('results');
  };

  const getRiskColor = (score) => {
    if (score >= 80) return { bg: 'bg-green-50', border: 'border-green-500', text: 'text-green-700', badge: 'bg-green-500' };
    if (score >= 60) return { bg: 'bg-yellow-50', border: 'border-yellow-500', text: 'text-yellow-700', badge: 'bg-yellow-500' };
    if (score >= 40) return { bg: 'bg-orange-50', border: 'border-orange-500', text: 'text-orange-700', badge: 'bg-orange-500' };
    return { bg: 'bg-red-50', border: 'border-red-500', text: 'text-red-700', badge: 'bg-red-500' };
  };

  const getRiskLabel = (riskLevel) => {
    const labels = {
      'LOW': '✅ BAJO RIESGO - Red Segmentada',
      'MEDIUM': '⚠️ RIESGO MEDIO - Segmentación Parcial',
      'HIGH': '🔴 ALTO RIESGO - Red Plana Detectada',
      'CRITICAL': '🚨 RIESGO CRÍTICO - Servicios Críticos Expuestos'
    };
    return labels[riskLevel] || riskLevel;
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'CRITICAL': 'bg-red-100 text-red-800 border-red-300',
      'HIGH': 'bg-orange-100 text-orange-800 border-orange-300',
      'MEDIUM': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'LOW': 'bg-blue-100 text-blue-800 border-blue-300'
    };
    return colors[severity] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          🛡️ Microsegmentación de Red (Ransomware)
        </h1>
        <p className="text-lg text-gray-600">
          Evalúa el nivel de segmentación de red y el riesgo de propagación lateral de ransomware
        </p>
      </div>

      {/* View Mode Tabs */}
      <div className="mb-6 flex gap-4">
        <button
          onClick={() => setViewMode('form')}
          className={`px-6 py-3 rounded-lg font-semibold transition-all ${
            viewMode === 'form'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          🚀 Nuevo Escaneo
        </button>
        <button
          onClick={() => setViewMode('history')}
          className={`px-6 py-3 rounded-lg font-semibold transition-all ${
            viewMode === 'history'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          📋 Historial ({scans.length})
        </button>
      </div>

      {/* Disclaimer Ético */}
      {viewMode === 'form' && (
        <div className="mb-8 bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg">
          <div className="flex items-start">
            <span className="text-3xl mr-4">ℹ️</span>
            <div>
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Escaneo Ético y Legal</h3>
              <p className="text-blue-800 text-sm">
                Este escaneo utiliza <strong>tests de DETECCIÓN no invasivos</strong> (no explotación). 
                Solo analiza información públicamente accesible y verifica indicadores de vulnerabilidad.
                Cumple con la Ley 26.388 Argentina (Art. 153 bis) y estándares OWASP.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Scan Form */}
      {viewMode === 'form' && (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <TargetSelect
            label="Dominio o IP a Escanear"
            value={target}
            onChange={setTarget}
            types={['domain', 'ip']}
            disabled={scanning}
            testid="microseg-target-select"
          />
          <div className="flex gap-4 mt-4">
            <button
              onClick={handleScan}
              disabled={scanning}
              className="px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {scanning ? '⏳ Escaneando...' : '🚀 Iniciar Escaneo'}
            </button>
          </div>
          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              ❌ {error}
            </div>
          )}
        </div>
      )}

      {/* Loading State */}
      {scanning && (
        <div className="bg-white rounded-lg shadow-lg p-12 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-xl font-semibold text-gray-900">Analizando superficie de ataque IA...</p>
          <p className="text-gray-600 mt-2">Esto puede tomar 30-60 segundos</p>
        </div>
      )}

      {/* History View */}
      {viewMode === 'history' && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Historial de Escaneos Microsegmentación de Red
          </h3>

          {scans.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No hay escaneos registrados aún.</p>
              <button
                onClick={() => setViewMode('form')}
                className="mt-4 text-blue-600 hover:underline"
              >
                Crear primer escaneo →
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {scans.map((scan) => {
                const aiScore = scan.results?.microsegmentation_score || 0;
                const riskLevel = scan.results?.risk_level || 'UNKNOWN';
                return (
                  <div
                    key={scan.id}
                    className="border border-gray-200 rounded-lg p-4 hover:border-blue-400 cursor-pointer transition"
                    onClick={() => viewScanDetails(scan)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900">{scan.target}</h4>
                        <p className="text-sm text-gray-500">
                          {new Date(scan.created_at).toLocaleString('es-AR')}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className={`text-2xl font-bold ${getRiskColor(aiScore).text}`}>
                          {aiScore}/100
                        </div>
                        <p className={`text-xs px-2 py-1 rounded ${getRiskColor(aiScore).bg}`}>
                          {getRiskLabel(riskLevel)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Results View */}
      {viewMode === 'results' && results && (
        <div className="space-y-8">
          {/* Back Button */}
          <button
            onClick={() => setViewMode('history')}
            className="text-blue-600 hover:text-blue-800 font-semibold"
          >
            ← Volver al historial
          </button>

          {/* Score Principal */}
          <div className={`rounded-lg shadow-xl p-8 border-2 ${getRiskColor(results.microsegmentation_score).border} ${getRiskColor(results.microsegmentation_score).bg}`}>
            <div className="text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Nivel de Segmentación de Red
              </h2>
              <div className={`text-9xl font-bold mb-4 ${getRiskColor(results.microsegmentation_score).text}`}>
                {results.microsegmentation_score}
                <span className="text-4xl text-gray-500">/100</span>
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-8 mb-6">
                <div
                  className={`h-8 rounded-full transition-all duration-500 ${getRiskColor(results.microsegmentation_score).badge} flex items-center justify-end pr-4`}
                  style={{ width: `${results.microsegmentation_score}%` }}
                >
                  <span className="text-white font-bold text-sm">{results.microsegmentation_score}%</span>
                </div>
              </div>

              {/* Risk Badge */}
              <div className={`inline-block px-8 py-4 rounded-full font-bold text-2xl ${getRiskColor(results.microsegmentation_score).badge} text-white shadow-lg`}>
                {getRiskLabel(results.risk_level)}
              </div>
              
              {/* Ransomware Risk */}
              {results.ransomware_propagation_probability && (
                <div className="mt-6 bg-red-50 border-2 border-red-300 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-red-900 mb-4">🔴 Riesgo de Propagación de Ransomware</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                    <div>
                      <p className="text-sm text-red-600 mb-1">Probabilidad</p>
                      <p className="text-2xl font-bold text-red-900">{results.ransomware_propagation_probability.probability_range}</p>
                    </div>
                    <div>
                      <p className="text-sm text-red-600 mb-1">Tiempo Contención</p>
                      <p className="text-lg font-bold text-red-900">{results.ransomware_propagation_probability.containment_time}</p>
                    </div>
                    <div>
                      <p className="text-sm text-red-600 mb-1">Pérdida Potencial</p>
                      <p className="text-lg font-bold text-red-900">{results.ransomware_propagation_probability.potential_loss}</p>
                    </div>
                    <div>
                      <p className="text-sm text-red-600 mb-1">Costo Estimado</p>
                      <p className="text-lg font-bold text-red-900">{results.ransomware_propagation_probability.cost_estimate}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Detailed Results */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="text-3xl mr-3">🎯</span>
              Análisis de Segmentación
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Open Ports */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <span className="text-2xl mr-2">🔌</span>
                  <span className="font-semibold text-gray-900">Puertos Abiertos</span>
                </div>
                <p className="text-3xl font-bold text-gray-900">
                  {results.detailed_results?.open_ports_count || 0}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {results.detailed_results?.port_exposure?.assessment || "Analyzing..."}
                </p>
              </div>

              {/* Internal Services */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <span className="text-2xl mr-2">🏢</span>
                  <span className="font-semibold text-gray-900">Servicios Internos Expuestos</span>
                </div>
                <p className={`text-3xl font-bold ${results.detailed_results?.exposed_internal_services?.count > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {results.detailed_results?.exposed_internal_services?.count || 0}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {results.detailed_results?.exposed_internal_services?.risk_level || "LOW"}
                </p>
              </div>

              {/* DMZ Architecture */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <span className="text-2xl mr-2">🌐</span>
                  <span className="font-semibold text-gray-900">Arquitectura</span>
                </div>
                <p className={`text-lg font-bold ${results.detailed_results?.dmz_architecture?.dmz_detected ? 'text-green-600' : 'text-red-600'}`}>
                  {results.detailed_results?.dmz_architecture?.architecture || "Unknown"}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {results.detailed_results?.dmz_architecture?.dmz_detected ? "DMZ Detectada" : "Red Plana"}
                </p>
              </div>

              {/* Firewall */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <span className="text-2xl mr-2">🔥</span>
                  <span className="font-semibold text-gray-900">Firewall</span>
                </div>
                <p className={`text-lg font-bold ${results.detailed_results?.firewall_detection?.firewall_detected ? 'text-green-600' : 'text-red-600'}`}>
                  {results.detailed_results?.firewall_detection?.assessment || "Unknown"}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {results.detailed_results?.firewall_detection?.closed_critical_ports || 0} puertos críticos cerrados
                </p>
              </div>

              {/* TTL Analysis */}
              {results.detailed_results?.ttl_analysis && !results.detailed_results?.ttl_analysis?.error && (
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center mb-2">
                    <span className="text-2xl mr-2">⏱️</span>
                    <span className="font-semibold text-gray-900">Análisis TTL</span>
                  </div>
                  <p className="text-lg font-bold text-gray-900">
                    {results.detailed_results.ttl_analysis.interpretation}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    Varianza: {results.detailed_results.ttl_analysis.ttl_variance}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Vulnerabilidades/Riesgos Detectados */}
          {results.vulnerabilities && results.vulnerabilities.length > 0 && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="text-3xl mr-3">⚠️</span>
                Vulnerabilidades y Riesgos Detectados ({results.vulnerabilities.length})
              </h3>
              <div className="space-y-4">
                {results.vulnerabilities.map((vuln, index) => (
                  <div key={index} className={`border-2 rounded-lg p-6 ${getSeverityColor(vuln.severity.toUpperCase())}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${getSeverityColor(vuln.severity.toUpperCase())}`}>
                            {vuln.severity.toUpperCase()}
                          </span>
                          <h4 className="text-lg font-bold text-gray-900">{vuln.title}</h4>
                        </div>
                        <p className="text-gray-700 whitespace-pre-line mb-4">{vuln.description}</p>
                        
                        {vuln.ai_exploitation && (
                          <div className="bg-white bg-opacity-50 rounded-lg p-4 mb-4">
                            <p className="text-sm font-semibold text-gray-900 mb-1">🤖 Explotación por IA:</p>
                            <p className="text-sm text-gray-800">{vuln.ai_exploitation}</p>
                          </div>
                        )}

                        {vuln.recommendation && (
                          <div className="bg-white bg-opacity-50 rounded-lg p-4">
                            <p className="text-sm font-semibold text-gray-900 mb-1">💡 Recomendación:</p>
                            <p className="text-sm text-gray-800">{vuln.recommendation}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recomendaciones Priorizadas */}
          {results.recommendations && results.recommendations.length > 0 && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="text-3xl mr-3">💡</span>
                Recomendaciones Priorizadas
              </h3>
              <div className="space-y-4">
                {results.recommendations.map((rec, index) => {
                  const priorityColors = {
                    'CRITICAL': 'bg-red-500',
                    'HIGH': 'bg-orange-500',
                    'MEDIUM': 'bg-yellow-500',
                    'LOW': 'bg-blue-500'
                  };
                  return (
                    <div key={index} className="border-2 border-gray-200 rounded-lg p-6">
                      <div className="flex items-start gap-4 mb-4">
                        <span className={`flex-shrink-0 w-10 h-10 ${priorityColors[rec.priority]} text-white rounded-full flex items-center justify-center font-bold text-lg`}>
                          {index + 1}
                        </span>
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className={`px-3 py-1 ${priorityColors[rec.priority]} text-white rounded-full text-xs font-bold`}>
                              {rec.priority}
                            </span>
                            <h4 className="text-xl font-bold text-gray-900">{rec.title}</h4>
                          </div>
                          <p className="text-gray-700 mb-4">{rec.description}</p>

                          {/* Steps */}
                          {rec.steps && rec.steps.length > 0 && (
                            <div className="bg-gray-50 rounded-lg p-4 mb-4">
                              <p className="font-semibold text-gray-900 mb-2">Pasos a seguir:</p>
                              <ul className="space-y-1 text-sm text-gray-800">
                                {rec.steps.map((step, i) => (
                                  <li key={i}>{step}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Métricas */}
                          <div className="grid grid-cols-3 gap-4">
                            <div className="bg-blue-50 rounded-lg p-3">
                              <p className="text-xs text-blue-600 font-semibold mb-1">Timeline</p>
                              <p className="text-sm font-bold text-blue-900">{rec.timeline}</p>
                            </div>
                            <div className="bg-green-50 rounded-lg p-3">
                              <p className="text-xs text-green-600 font-semibold mb-1">Costo</p>
                              <p className="text-sm font-bold text-green-900">{rec.cost}</p>
                            </div>
                            <div className="bg-purple-50 rounded-lg p-3">
                              <p className="text-xs text-purple-600 font-semibold mb-1">Impacto</p>
                              <p className="text-sm font-bold text-purple-900">{rec.ai_defense_impact}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Contexto de Amenazas IA */}
          {results.threat_intelligence && (
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg shadow-lg p-6 border-2 border-purple-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="text-3xl mr-3">🚨</span>
                Contexto de Amenazas IA Actuales
              </h3>

              {/* IAs Maliciosas Activas */}
              {results.threat_intelligence.active_ai_threats && (
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">IAs Maliciosas Activas:</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {results.threat_intelligence.active_ai_threats.map((threat, index) => (
                      <div key={index} className="bg-white rounded-lg p-4 border border-purple-200">
                        <h5 className="font-bold text-purple-900 text-lg mb-2">{threat.name}</h5>
                        <p className="text-sm text-gray-700 mb-2">{threat.capability}</p>
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-gray-600">Precio: {threat.price}</span>
                          <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded">{threat.target_sectors.join(', ')}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Incidentes Recientes */}
              {results.threat_intelligence.recent_incidents && (
                <div className="bg-white bg-opacity-50 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Incidentes Recientes:</h4>
                  <ul className="space-y-2 text-sm text-gray-800">
                    {results.threat_intelligence.recent_incidents.map((incident, index) => (
                      <li key={index} className="flex items-start">
                        <span className="mr-2">🔴</span>
                        <span>{incident}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MicrosegmentationScanner;
